/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import {
  Box,
  Checkbox,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { debounce } from 'lodash';
import React, { useState } from 'react';
import type { ReactNode } from 'react';
import { useSharedTranslation } from '../../utils/i18n';
import { CustomIconButton } from '../CustomIconButton';

interface Props {
  optionId: string;
  checked: boolean;
  onClick: (checked: boolean, only?: boolean) => void;
  labelStyle?: SxProps<Theme>;
  label: ReactNode;
}

export const FilterOption: React.FC<Props> = ({
  optionId,
  checked,
  onClick,
  labelStyle,
  label,
}: Props) => {
  const { t } = useSharedTranslation();
  const [showOnly, setShowOnly] = useState(false);
  const updateShowOnly = debounce((show) => setShowOnly(show), 100);

  return (
    <ListItem
      dense
      disablePadding
      secondaryAction={
        showOnly ? (
          <CustomIconButton
            aria-label={`Only ${label}`}
            edge="end"
            onClick={() => onClick(!checked, true)}
            onMouseOut={() => updateShowOnly(false)}
            onMouseOver={() => updateShowOnly(true)}
            style={{ backgroundColor: 'transparent', width: 'auto' }}
          >
            <Typography
              sx={{
                color: 'geowebColors.buttons.primary.default.fill',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              {t('shared-only')}
            </Typography>
          </CustomIconButton>
        ) : null
      }
      sx={{
        width: '100%',
        minWidth: '300px',
        paddingRight: '0px',
        marginLeft: '-12px',
        fontSize: '12px',
      }}
    >
      <ListItemButton
        dense
        disableGutters
        onClick={() => onClick(!checked)}
        onMouseOut={() => updateShowOnly(false)}
        onMouseOver={() => updateShowOnly(true)}
        role={undefined}
        sx={{
          padding: 0,
        }}
      >
        <ListItemIcon sx={{ width: '42px' }}>
          <Checkbox
            checked={checked}
            disableRipple
            name={optionId}
            slotProps={{
              input: {
                'aria-labelledby': optionId,
                'aria-label': optionId,
              },
            }}
            tabIndex={-1}
          />
        </ListItemIcon>
        <ListItemText
          id={optionId}
          primary={<Box sx={labelStyle}>{label}</Box>}
        />
      </ListItemButton>
    </ListItem>
  );
};
